import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { AgentHeader } from "@/components/AgentHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PageLoader, PageHeader } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { ReminderEditor } from "@/components/ReminderEditor";
import { WelcomeEditor } from "@/components/WelcomeEditor";
import { MilestoneSettings } from "@/components/MilestoneSettings";
import { UserPlus, Loader2, Copy, Users, MessageCircleQuestion, Check, AlertTriangle, Clock, Pause, Play, Link2, MessageSquare } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-status-complete/15 text-status-complete",
  invited: "bg-status-warning/15 text-status-warning",
  inactive: "bg-muted text-muted-foreground",
  archived: "bg-muted text-muted-foreground line-through",
};

export default function AgentDashboard() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [invite, setInvite] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filter, setFilter] = useState("active");
  const [toPause, setToPause] = useState(null);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["agent-clients"], queryFn: async () => (await api.get("/agent/clients")).data,
  });
  const { data: insights } = useQuery({
    queryKey: ["agent-insights"], queryFn: async () => (await api.get("/agent/insights")).data,
  });

  const createClient = useMutation({
    mutationFn: async () => (await api.post("/agent/clients", form)).data,
    onSuccess: (data) => { qc.invalidateQueries({ queryKey: ["agent-clients"] }); setInvite(data); setForm({ name: "", email: "", phone: "" }); toast.success("Client workspace created"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const copyLink = () => { navigator.clipboard.writeText(invite.invite_link); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  const copyText = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Couldn't copy — try again"); }
  };
  const inviteLink = (c) => c.invite_link || `${window.location.origin}/invite?code=${c.invite_code}`;
  const inviteMsg = (c) => `Hi ${(c.name || "there").split(" ")[0]}! I set up your private home-buying workspace — Look Before You Leap 808. Tap to open it (no password needed): ${inviteLink(c)}  — Marlene, eXp Realty`;

  const setActive = useMutation({
    mutationFn: async ({ id, deactivated }) => (await api.put(`/agent/clients/${id}/deactivate`, { deactivated })).data,
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: ["agent-clients"] });
      qc.invalidateQueries({ queryKey: ["agent-insights"] });
      toast.success(v.deactivated ? "Client paused — their portal is now inactive" : "Client reactivated — their link works again");
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (isLoading) return (<div className="min-h-screen bg-background"><AgentHeader /><PageLoader /></div>);

  const activeCount = clients.filter((c) => c.status === "active" && !c.deactivated).length;
  const inactiveClients = clients.filter((c) => c.deactivated);
  const activeList = clients.filter((c) => !c.deactivated);
  const shown = filter === "inactive" ? inactiveClients : activeList;
  const openQ = clients.reduce((a, c) => a + (c.open_questions || 0), 0);

  return (
    <div className="min-h-screen bg-background">
      <AgentHeader />
      <main className="max-w-6xl mx-auto p-4 sm:p-8">
        <PageHeader title="Your clients" subtitle="Invite buyers, watch their readiness, and step in at the right moments.">
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setInvite(null); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full gap-2" data-testid="agent-invite-client-btn"><UserPlus className="h-4 w-4" /> Invite client</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Invite a client</DialogTitle>
                <DialogDescription>Create a workspace and share the invite link. They'll link to it when they sign up.</DialogDescription>
              </DialogHeader>
              {!invite ? (
                <div className="space-y-3">
                  <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="invite-name-input" /></div>
                  <div className="space-y-1.5"><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="invite-email-input" /></div>
                  <div className="space-y-1.5"><Label>Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="invite-phone-input" /></div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm">Share this link with <strong>{invite.name}</strong>:</p>
                  <div className="flex gap-2">
                    <Input readOnly value={invite.invite_link} data-testid="invite-link-output" className="text-xs" />
                    <Button onClick={copyLink} variant="outline" data-testid="copy-invite-btn">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Invite code: <code className="bg-secondary px-1.5 py-0.5 rounded">{invite.invite_code}</code></p>
                </div>
              )}
              <DialogFooter>
                {!invite ? (
                  <Button onClick={() => createClient.mutate()} disabled={!form.name || !form.email || createClient.isPending} data-testid="invite-create-btn" className="rounded-full">
                    {createClient.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & get link"}
                  </Button>
                ) : (
                  <Button onClick={() => { setOpen(false); setInvite(null); }} className="rounded-full">Done</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </PageHeader>

        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          {[
            { label: "Total clients", value: clients.length, icon: Users },
            { label: "Active", value: activeCount, icon: Check },
            { label: "Open questions", value: openQ, icon: MessageCircleQuestion },
          ].map((s) => (
            <Card key={s.label} className="p-5 rounded-2xl flex items-center gap-4">
              <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><s.icon className="h-5 w-5" /></div>
              <div><p className="text-xs uppercase tracking-wider text-muted-foreground">{s.label}</p><p className="font-heading text-2xl font-bold">{s.value}</p></div>
            </Card>
          ))}
        </div>

        {insights && (insights.attention?.length > 0) && (
          <Card className="p-6 rounded-2xl mb-6 border-status-warning/30 bg-status-warning/5" data-testid="engagement-insights">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <AlertTriangle className="h-5 w-5 text-status-warning" />
              <h3 className="font-heading font-semibold text-lg">Needs your attention</h3>
              <Badge className="bg-status-warning/15 text-status-warning border-0">
                {insights.totals.stalled} stalled · {insights.totals.awaiting_onboarding} awaiting onboarding
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Clients are flagged "stalled" after {insights.stall_days} days with no activity. Tap a client to step in.
            </p>
            <div className="space-y-2">
              {insights.attention.map((a, i) => (
                <div key={a.id} onClick={() => navigate(`/agent/clients/${a.id}`)} data-testid={`attention-row-${i}`}
                  className="flex items-center gap-3 p-3 rounded-xl bg-card hover:shadow-sm cursor-pointer transition-shadow">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{a.name}</p>
                    <p className="text-xs text-muted-foreground">Stage: {a.current_stage_title} · {a.progress_pct}% done</p>
                  </div>
                  {a.open_questions > 0 && <Badge className="bg-primary/15 text-primary border-0">{a.open_questions} question{a.open_questions > 1 ? "s" : ""}</Badge>}
                  {a.days_inactive != null && (
                    <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0"><Clock className="h-3 w-3" /> {a.days_inactive}d idle</span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {clients.length === 0 ? (
          <Card className="p-12 rounded-2xl text-center">
            <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-heading text-lg font-semibold">No clients yet</p>
            <p className="text-muted-foreground text-sm mt-1">Invite your first buyer to get started.</p>
          </Card>
        ) : (
          <>
          <div className="flex gap-2 mb-4" data-testid="client-filter">
            <Button variant={filter === "active" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setFilter("active")} data-testid="filter-active-btn">Active ({activeList.length})</Button>
            <Button variant={filter === "inactive" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setFilter("inactive")} data-testid="filter-inactive-btn">Inactive ({inactiveClients.length})</Button>
          </div>
          {shown.length === 0 ? (
            <Card className="p-10 rounded-2xl text-center text-muted-foreground" data-testid="clients-empty-filter">
              {filter === "inactive" ? "No inactive clients." : "No active clients."}
            </Card>
          ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {shown.map((c, i) => {
              const pct = Math.round((c.roadmap.stages.filter((s) => s.status === "complete").length / c.roadmap.stages.length) * 100);
              return (
                <motion.div key={c.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  <Card onClick={() => navigate(`/agent/clients/${c.id}`)} className={`p-5 rounded-2xl cursor-pointer hg-card-hover ${c.deactivated ? "opacity-70" : ""}`} data-testid={`agent-client-row-${i + 1}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-heading font-semibold text-lg">{c.name}</p>
                        <p className="text-sm text-muted-foreground">{c.email}</p>
                      </div>
                      {c.deactivated
                        ? <Badge className="border-0 bg-muted text-muted-foreground" data-testid={`client-paused-badge-${c.id}`}>Paused</Badge>
                        : <Badge className={`border-0 capitalize ${STATUS_STYLES[c.status] || ""}`}>{c.status}</Badge>}
                    </div>
                    <div className="mt-4">
                      <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">Current: {c.roadmap.stages.find((s) => s.key === c.roadmap.current_stage)?.title}</span><span className="font-medium">{pct}%</span></div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                    <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                      <span>Readiness {c.roadmap.checklist_done}/{c.roadmap.checklist_total}</span>
                      <span>Homes {c.roadmap.homes_count}</span>
                      {c.open_questions > 0 && <span className="text-status-warning font-medium">{c.open_questions} open question{c.open_questions > 1 ? "s" : ""}</span>}
                      {c.deactivated ? (
                        <Button variant="outline" size="sm" className="ml-auto rounded-full h-7 gap-1" onClick={(e) => { e.stopPropagation(); setActive.mutate({ id: c.id, deactivated: false }); }} data-testid={`reactivate-btn-${c.id}`}>
                          <Play className="h-3 w-3" /> Reactivate
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" className="ml-auto rounded-full h-7 gap-1 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); setToPause(c); }} data-testid={`deactivate-btn-${c.id}`}>
                          <Pause className="h-3 w-3" /> Deactivate
                        </Button>
                      )}
                    </div>
                    {!c.deactivated && (
                      <div className="flex flex-wrap items-center gap-2 mt-4 pt-3 border-t border-border">
                        <Button variant="outline" size="sm" className="rounded-full h-8 gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); copyText(inviteLink(c), "Invite link copied"); }} data-testid={`copy-link-${c.id}`}>
                          <Link2 className="h-3.5 w-3.5" /> Copy invite link
                        </Button>
                        <Button variant="outline" size="sm" className="rounded-full h-8 gap-1.5 text-xs" onClick={(e) => { e.stopPropagation(); copyText(inviteMsg(c), "Text message copied — paste into Messages"); }} data-testid={`copy-text-${c.id}`}>
                          <MessageSquare className="h-3.5 w-3.5" /> Copy text message
                        </Button>
                      </div>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>
          )}
          </>
        )}

        <AlertDialog open={!!toPause} onOpenChange={(v) => { if (!v) setToPause(null); }}>
          <AlertDialogContent data-testid="deactivate-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Deactivate {toPause?.name}?</AlertDialogTitle>
              <AlertDialogDescription>
                Their portal link and login will stop working until you reactivate them. Nothing is deleted — all their homes, notes, ratings and checklists stay saved. No email or text is sent to them.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="deactivate-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { setActive.mutate({ id: toPause.id, deactivated: true }); setToPause(null); }} data-testid="deactivate-confirm-btn">
                Deactivate
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <WelcomeEditor />
        <MilestoneSettings />
        <ReminderEditor />

        <div className="mt-8"><Disclaimer /></div>
      </main>
    </div>
  );
}
